// The window every page and every modal is looking through.
//
// THIS IS THE FIX FOR THE DEAD-END MODAL. Before, the period select and the
// date picker lived in the page header and nothing else could reach them: once
// a drill-down was open, the only way to see the same drill-down a week earlier
// was to close it, change the date, and open it again — by which point you had
// forgotten what you were comparing. The window is now one shared model, the
// modal shell renders the same controls the page header does, and changing the
// window refetches whatever is open *in place*, keeping its subject.
//
// It is deliberately a module-level singleton rather than a provide/inject.
// There is exactly one window per dashboard — a modal showing a different week
// from the page behind it would be a bug, not a feature — and a singleton makes
// that structurally true instead of a convention.
//
// The URL is the source of truth, so every state this dashboard can be in is a
// link someone can paste into Slack. `web/periods.js` does the date maths, and
// it is the same module the Worker imports, so a URL the browser writes and a
// window the Worker computes cannot drift apart.

import { computed, ref } from 'vue'
import { PERIODS, anchorFor, dayInWindow, todayJst, latestWithScore } from '../../periods.js'
import { windowLabel, optionLabel } from '../lib/periodLabels.js'
import { fetchJson } from './useApi.js'
import { tk } from '../lib/i18n.js'
import { scope } from '../lib/scope.js'

export { PERIODS }

/** How a period reads on the segmented control. */
export const PERIOD_LABEL = {
  daily: tk('Day'),
  weekly: tk('Week'),
  monthly: tk('Month'),
  quarterly: tk('Quarter'),
  yearly: tk('Year'),
}

/** Coarser and finer, for the ⇧← / ⇧→ shortcut and the zoom buttons. */
const coarser = (period) => PERIODS[Math.min(PERIODS.length - 1, PERIODS.indexOf(period) + 1)]
const finer = (period) => PERIODS[Math.max(0, PERIODS.indexOf(period) - 1)]

/** Which windows actually hold rows. Loaded once; the picker cannot offer a
 *  window nobody measured, and `step()` cannot land on one. */
const available = ref(null)
const availableError = ref(null)
let loading = null
/** Whose days `available` holds: Direct's, or the Hotel site view's (lib/scope.js). */
let loadedFor = null

/** How long to wait before the one automatic retry of /api/periods. */
export const AVAILABLE_RETRY_MS = 1500

export function loadAvailable({ retryAfter = AVAILABLE_RETRY_MS } = {}) {
  // Each view has its own days: the hotel sites were first measured later.
  const want = scope()
  if (loading && loadedFor === want) return loading
  if (loadedFor !== want) available.value = null
  loadedFor = want
  availableError.value = null
  // Once more on its own before saying anything: the picker, the header and
  // "Latest" all wait on this one request, and a single dropped request used
  // to leave all three stuck until a reload. Board DG1.
  const once = () => fetchJson(want ? `/api/periods?scope=${want}` : '/api/periods')
  loading = once()
    .catch(() => new Promise((resolve) => setTimeout(resolve, retryAfter)).then(once))
    .then((body) => {
      available.value = body
      return body
    })
    .catch((err) => {
      // A picker with no data behind it still has to say so. GUARDRAILS §5.
      availableError.value = err
      loading = null
      throw err
    })
  return loading
}

let router = null

/** Called once from App.vue. The router is how the window reads and writes the URL. */
export function bindWindowToRouter(instance) {
  router = instance
}

const DEFAULTS = () => ({ period: 'daily', date: todayJst(), pages: null })

/** Fallback for the window before the router has mounted (and in unit tests). */
const detached = ref(DEFAULTS())

/**
 * The reader's own day: the last day they chose, kept apart from the URL's
 * anchor, which snaps to the start of a week, month, quarter or year. Switching
 * period goes through it (`dayInWindow`), so daily 6 Oct → weekly, monthly or
 * yearly → back down lands on 6 Oct again, not on the 5th or the 1st. Set by a
 * day on the daily view, a day clicked in a weekly calendar, and "Latest" (the
 * newest day with a score). Not in the URL: it is where the reader was, not a
 * window anyone would link to.
 */
const focus = ref(null)

function current() {
  const query = router?.currentRoute.value.query
  if (!query) return detached.value
  const period = PERIODS.includes(query.period) ? query.period : DEFAULTS().period
  const raw =
    typeof query.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(query.date) ? query.date : null
  return {
    period,
    // Snapped to the window's own anchor, so a pasted `?period=weekly&date=
    // 2026-10-03` (a Saturday) names the week that holds it — the same window
    // the Worker computes — and matches the picker's options instead of
    // leaving it with nothing selected.
    date: anchorFor(period, raw ?? DEFAULTS().date),
    // `null` is every page in the window, which is not the same as "all four":
    // a page added tomorrow must appear without anyone editing a URL, so only
    // an explicit subset is ever written down. docs/CHARTS.md.
    pages:
      typeof query.pages === 'string' && query.pages
        ? query.pages.split(',').filter(Boolean)
        : null,
  }
}

function write(next, { replace = false } = {}) {
  const merged = { ...current(), ...next }
  // Always store the canonical anchor. Two URLs naming the same week must be
  // the same URL, or the fetch cache keys off two strings for one window.
  merged.date = anchorFor(merged.period, merged.date)
  // On the daily view the window IS a day: that is the reader's day now.
  if (merged.period === 'daily') focus.value = merged.date

  if (!router) {
    detached.value = merged
    return
  }

  const query = { ...router.currentRoute.value.query }
  query.period = merged.period
  query.date = merged.date
  if (merged.pages?.length) query.pages = merged.pages.join(',')
  else delete query.pages

  const go = replace ? router.replace : router.push
  go.call(router, { query })
}

export function useWindow() {
  const period = computed(() => current().period)
  const date = computed(() => current().date)
  const pages = computed(() => current().pages)

  const label = computed(() => windowLabel(period.value, date.value))

  /** Every stored window for the current period, newest first. */
  const options = computed(() => available.value?.options?.[period.value] ?? [])

  /** Where the current anchor sits in that list. -1 when the window holds no
   *  rows, which is a real state: a link to a day nothing was measured on. */
  const index = computed(() => options.value.findIndex((o) => o.value === date.value))

  /** The days that kept the full waterfall. docs/decisions/0014. */
  const deepDays = computed(() => new Set(available.value?.deepDays ?? []))

  const hasRows = computed(() => index.value !== -1)

  // Options are newest-first, so "older" is a HIGHER index. Naming them back
  // and forward rather than up and down keeps the arrows honest.
  const canStepBack = computed(() => index.value !== -1 && index.value < options.value.length - 1)
  const canStepForward = computed(() => index.value > 0)

  /**
   * Move one stored window, skipping the gaps.
   *
   * Stepping by the calendar instead would walk onto days nothing was measured
   * — a weekend the run failed, a month before the sample existed — and the
   * reader would read an empty panel as "we got slower" rather than as "there
   * is nothing here".
   */
  function step(delta) {
    if (!options.value.length) return
    const from = index.value === -1 ? 0 : index.value
    const target = from - delta
    const clamped = Math.max(0, Math.min(options.value.length - 1, target))
    if (clamped === index.value) return
    write({ date: options.value[clamped].value })
  }

  /**
   * Switch granularity, staying on the reader's own day: the window of the new
   * period that holds it. Going back down finds the same day again.
   */
  function setPeriod(next) {
    if (!PERIODS.includes(next) || next === period.value) return
    // On the daily view the URL's date is the day, however the reader got
    // there — a pasted link or Back included. Anywhere else, the day they
    // chose, placed in the window on screen.
    const day =
      period.value === 'daily' ? date.value : dayInWindow(period.value, date.value, focus.value)
    focus.value = day
    write({ period: next, date: anchorFor(next, day) })
  }

  const zoomOut = () => setPeriod(coarser(period.value))
  const zoomIn = () => setPeriod(finer(period.value))

  /**
   * `replace` for a date the reader did not pick (the Overview's newest good
   * day). `day` is the day the reader chose when it is more than the window's
   * start — a day clicked in a weekly calendar, or "Latest" — and becomes
   * their day for the next period change.
   */
  function setDate(value, { replace = false, day = null } = {}) {
    if (day) focus.value = day
    if (!value || anchorFor(period.value, value) === date.value) return
    write({ date: value }, { replace })
  }

  /** `replace` for a correction the reader did not make (a stale filter dropped). */
  function setPages(list, { replace = false } = {}) {
    // The last selected page cannot be switched off — an empty chart is not a
    // state the filter is allowed to reach. docs/CHARTS.md.
    if (list && !list.length) return
    write({ pages: list }, { replace })
  }

  /** The newest window with a score. Where the dashboard opens. */
  function jumpToLatest() {
    const newest = latestWithScore(available.value, period.value)
    // The newest day with a score lies in that window: it is the reader's day.
    const day = latestWithScore(available.value, 'daily')
    if (day && anchorFor(period.value, day) === newest) focus.value = day
    if (newest) write({ date: newest }, { replace: true })
  }

  return {
    period,
    date,
    pages,
    label,
    options,
    index,
    deepDays,
    hasRows,
    available,
    availableError,
    canStepBack,
    canStepForward,
    step,
    setPeriod,
    setDate,
    setPages,
    zoomIn,
    zoomOut,
    jumpToLatest,
    optionLabel,
    PERIOD_LABEL,
    PERIODS,
  }
}

// Module-level singleton state: a hot update would hand new refs to some
// components while others keep the old ones, and the page would half-work until
// a manual reload. Escalate the update to a full reload instead.
if (import.meta.hot) {
  import.meta.hot.accept(() => import.meta.hot.invalidate())
}
